import { CASES } from "@/data/results";
import { slug } from "@/data/nav";
import { Reveal } from "@/components/site/Motion";
import { Label, H2, Section } from "@/components/site/Blocks";

const Metrics = ({ items, big }) => (
  <dl className={`grid gap-x-6 gap-y-5 ${big ? "grid-cols-2" : "grid-cols-2"}`}>
    {items.map(([v, l]) => (
      <div key={v + l} className="border-t border-slate-200 pt-4">
        <dt className={`font-display font-semibold text-slate-900 ${big ? "text-4xl sm:text-5xl" : "text-3xl"}`}>{v}</dt>
        <dd className="mt-1 text-[13.5px] leading-snug text-slate-600">{l}</dd>
      </div>
    ))}
  </dl>
);

const Frame = ({ c, className = "" }) => (
  <div className={`relative overflow-hidden bg-[#f1eff8] ${className}`}>
    <img src={c.img} alt={c.name} loading="lazy"
      className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${c.fit === "contain" ? "object-contain p-4" : "object-cover"}`} />
  </div>
);

const Card = ({ c }) => (
  <div data-testid={`case-card-${c.key || slug(c.name)}`} className="glow-card group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(76,29,149,0.35)]">
    <Frame c={c} className="aspect-square" />
    <div className="flex flex-1 flex-col p-6 sm:p-7">
      <h3 className="font-display text-2xl font-medium text-slate-900">{c.name}</h3>
      {c.desc && <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">{c.desc}</p>}
      {c.metrics && <div className="mt-6"><Metrics items={c.metrics} /></div>}
    </div>
  </div>
);

const Featured = ({ c }) => (
  <Reveal data-testid="case-featured" className="group grid overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-12">
    <Frame c={c} className="aspect-[16/10] lg:col-span-7 lg:aspect-auto lg:min-h-[460px]" />
    <div className="p-7 sm:p-10 lg:col-span-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-violet-700">Featured</p>
      <h3 className="mt-3 font-display text-3xl font-medium text-slate-900 sm:text-4xl">{c.name}</h3>
      <div className="mt-8"><Metrics items={c.metrics} big /></div>
    </div>
  </Reveal>
);

export const CaseStudies = () => {
  const featured = CASES.find((c) => c.featured);
  const list = CASES.filter((c) => !c.featured);
  return (
    <Section tone="subtle" testid="case-studies-section" id="case-studies">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Reveal><Label n="01">Case studies</Label></Reveal>
          <Reveal delay={0.05}><H2 className="mt-6">Client <span className="font-serif-i text-gradient-dark">Case Studies</span></H2></Reveal>
        </div>
      </div>
      <div className="mt-12"><Featured c={featured} /></div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="case-grid">
        {list.map((c, i) => (
          <Reveal key={c.key || c.name} delay={(i % 3) * 0.06} className="h-full">
            <Card c={c} />
          </Reveal>
        ))}
      </div>
    </Section>
  );
};
